import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/Button';
import CardPaymentSection from '../components/CardPaymentSection';
import DevCardElement from '../components/DevCardElement';
import FlagBookingModal from '../components/FlagBookingModal';
import BookingChanges from '../components/pro/BookingChanges';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import { proService, stripeService } from '../services';
import { isStripeDevMode, mockConfirmCardSetup } from '../utils/stripeDevMode';
import { SPECIES } from '../theme/species';
import { bookingWhen } from '../utils/proBookingTime';

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const when = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null);

const STATUS_COPY = {
  sent: 'Offer sent',
  countered: 'Counter on the table',
  accepted: 'Price agreed',
  confirmed: 'Booked',
  declined: 'Declined',
  expired: 'Expired',
  canceled: 'Canceled',
};

/**
 * ProOfferPage — the negotiation surface for one offer (6b). Shows the round
 * timeline + shot clock, and the right affordances for whoever's viewing:
 * accept / counter / decline while live, and — for the buyer once a price is
 * agreed — Proceed, which collects a card and holds the amount in escrow.
 */
export default function ProOfferPage() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { user, logout } = useAuth();
  const pink = SPECIES.partyStarter;

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [countering, setCountering] = useState(false);
  const [counterAmount, setCounterAmount] = useState('');
  const [counterNote, setCounterNote] = useState('');

  const [paying, setPaying] = useState(false);
  const [showFlag, setShowFlag] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [devBehavior, setDevBehavior] = useState('success');
  const cardRef = useRef(null);

  const load = useCallback(async () => {
    const r = await proService.getOffer(offerId);
    if (r.success && r.data) setOffer(r.data); else setNotFound(true);
    setLoading(false);
  }, [offerId]);
  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const run = async (fn) => {
    setBusy(true); setError('');
    const r = await fn();
    setBusy(false);
    if (r.success) { setOffer(r.data); setCountering(false); setCounterAmount(''); setCounterNote(''); }
    else setError(r.error || 'Something went wrong.');
  };

  const doAccept = () => run(() => proService.acceptOffer(offerId));
  const doDecline = () => run(() => proService.declineOffer(offerId));
  const doCounter = () => {
    const cents = Math.round(parseFloat(counterAmount) * 100);
    if (!cents || cents < 100) { setError('Counter must be at least $1.'); return; }
    return run(() => proService.counterOffer(offerId, { amountCents: cents, note: counterNote.trim() || null }));
  };

  // Escrow-verified testimonial (6c).
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const doReview = async (bookingId) => {
    setBusy(true); setError('');
    const r = await proService.leaveTestimonial(bookingId, { rating: reviewRating, text: reviewText.trim() || null });
    setBusy(false);
    if (r.success) await load(); else setError(r.error || 'Could not post your review.');
  };

  // Dispute ladder (6d): the flagged party responds, or the filer withdraws.
  const doRespond = async (flagId, action, offerToBuyerCents = 0) => {
    setBusy(true); setError('');
    const r = await proService.respondFlag(flagId, { action, offerToBuyerCents });
    setBusy(false);
    if (r.success) await load(); else setError(r.error || 'Could not respond.');
  };
  const doWithdraw = async (flagId) => {
    setBusy(true); setError('');
    const r = await proService.withdrawFlag(flagId);
    setBusy(false);
    if (r.success) await load(); else setError(r.error || 'Could not withdraw.');
  };

  const doProceed = async () => {
    setPaying(true); setError('');
    try {
      let stripePaymentMethodId = null;
      let stripeCustomerId = null;
      if (isStripeDevMode()) {
        const mock = await mockConfirmCardSetup(devBehavior);
        if (mock.error) { setError(mock.error.message); setPaying(false); return; }
        stripePaymentMethodId = mock.setupIntent.payment_method;
        stripeCustomerId = `cus_dev_${Date.now()}`;
      } else {
        const setupRes = await stripeService.createSetupIntent();
        if (!setupRes.success) { setError(setupRes.error || 'Could not prepare payment.'); setPaying(false); return; }
        if (!cardRef.current) { setError('Card form is not ready.'); setPaying(false); return; }
        const result = await cardRef.current.confirmSetup(setupRes.data.clientSecret, { email: profile?.email, name: profile?.displayName });
        if (result.error) { setError(result.error); setPaying(false); return; }
        stripePaymentMethodId = result.paymentMethod;
        stripeCustomerId = setupRes.data.customerId;
      }
      const r = await proService.proceedOffer(offerId, {
        stripePaymentMethodId, stripeCustomerId, email: profile?.email || offer.buyerEmail, name: profile?.displayName || null,
      });
      if (r.success) setOffer(r.data);
      else setError(r.error || 'Payment failed.');
    } finally {
      setPaying(false);
    }
  };

  /**
   * Apple Pay / Google Pay. The wallet has already authorised a PaymentMethod, so
   * there is no SetupIntent step — it goes straight to proceedOffer, and the
   * sheet is dismissed with the outcome via `complete`.
   *
   * The button used to render with no handler at all: the sheet would open, take
   * the card, and have nowhere to send it, so it sat on "Processing Payment"
   * while nothing was ever charged.
   */
  const doWalletProceed = async (walletPaymentMethod, complete) => {
    setPaying(true); setError('');
    try {
      const r = await proService.proceedOffer(offerId, {
        stripePaymentMethodId: walletPaymentMethod.id,
        // No SetupIntent here, so no customer yet — the server attaches the
        // payment method to one itself when this is absent.
        stripeCustomerId: null,
        email: profile?.email || offer.buyerEmail,
        name: profile?.displayName || null,
      });
      // Dismiss the sheet before touching state, so it never hangs behind a
      // re-render.
      complete(r.success ? 'success' : 'fail');
      if (r.success) setOffer(r.data);
      else setError(r.error || 'Payment failed.');
    } catch (e) {
      complete('fail');
      setError(e?.message || 'Payment failed.');
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-inside-bg">
        <Navbar user={profile || user} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/featured')} />
        <div className="mx-auto max-w-2xl p-10"><LoadingSpinner label="Loading…" /></div>
      </div>
    );
  }
  if (notFound || !offer) {
    return (
      <div className="min-h-screen bg-inside-bg">
        <Navbar user={profile || user} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/featured')} />
        <div className="mx-auto max-w-2xl p-10 text-center font-mono text-sm text-gray-500">This offer isn't available.</div>
      </div>
    );
  }

  const { isOwner, status, currentTurn } = offer;
  // A plain member of the page reads the booking (the dashboard's BOOKINGS links here) but acts on nothing.
  const readOnly = offer.viewerRole === 'member';
  const isBuyer = !isOwner && !readOnly;
  const myTurn = (isOwner && currentTurn === 'owner') || (isBuyer && currentTurn === 'buyer');
  const isLive = ['sent', 'countered'].includes(status);
  const canNegotiate = isLive && myTurn;
  const buyerCanPay = isBuyer && status === 'accepted';
  const terminal = ['declined', 'expired', 'canceled'].includes(status);

  return (
    <div className="min-h-screen bg-inside-bg">
      <Navbar user={profile || user} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/featured')} />
      <div className="mx-auto max-w-2xl px-4 py-6">
        {/* Header */}
        <div className="border-2 border-black bg-white p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: pink }}>
                {isOwner || readOnly ? 'Booking offer' : 'Your offer'}
              </div>
              <button onClick={() => navigate(`/pro/${offer.businessSlug}`)} className="mt-1 block font-mono text-2xl font-bold uppercase tracking-wider hover:underline">
                {offer.businessName}
              </button>
            </div>
            <span
              className="flex-shrink-0 border-2 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wide"
              style={status === 'confirmed'
                ? { background: pink, borderColor: pink, color: '#000' }
                : terminal ? { borderColor: '#9ca3af', color: '#9ca3af' } : { borderColor: '#000', color: '#000' }}
            >
              {STATUS_COPY[status] || status}
            </span>
          </div>

          <div className="mt-4 flex items-end justify-between border-t-2 border-black pt-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-gray-500">On the table</div>
              <div className="font-mono text-3xl font-bold">{money(offer.currentAmountCents)}</div>
            </div>
            {(offer.startsAt || offer.eventDate) && (
              <div className="text-right">
                <div className="font-mono text-[10px] uppercase tracking-wider text-gray-500">{offer.startsAt ? 'When' : 'Date'}</div>
                <div className="font-mono text-sm font-bold" data-testid="offer-when">{bookingWhen(offer, { year: true })}</div>
              </div>
            )}
          </div>

          {offer.sessionCount > 1 && (
            <div className="mt-4 border-t-2 border-black pt-4" data-testid="offer-sessions">
              <div className="font-mono text-[10px] uppercase tracking-wider text-gray-500">
                {offer.sessionCount} sessions{offer.discountPercent > 0 && offer.listAmountCents ? ` · ${offer.discountPercent}% off ${money(offer.listAmountCents)}` : ''}
              </div>
              <ul className="mt-2 space-y-1">
                {offer.sessions.map((s) => (
                  <li key={s.number} className="font-mono text-xs">
                    <span className="font-bold">{s.number}.</span>{' '}
                    {new Date(s.startsAt).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    {offer.booking?.releasedSessions >= s.number && <span className="font-bold" style={{ color: '#15803D' }}> · PAID OUT</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isLive && (
            <p className="mt-3 font-mono text-[11px] text-gray-500">
              {myTurn ? 'Your move.' : `Waiting on the ${currentTurn === 'owner' ? 'owner' : 'buyer'}.`}
              {' '}Expires {when(offer.expiresAt)}.
            </p>
          )}
        </div>

        {/* Timeline */}
        <div className="mt-4 border-2 border-black bg-white">
          <div className="border-b-2 border-black px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest">Negotiation</div>
          {offer.rounds.map((r) => (
            <div key={r.id} className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-2.5">
              <div className="min-w-0">
                <span className="font-mono text-[11px] font-bold uppercase tracking-wide" style={{ color: r.actor === 'owner' ? pink : '#000' }}>
                  {r.actor === 'owner' ? 'Owner' : 'Buyer'}
                </span>
                <span className="ml-2 font-mono text-[11px] text-gray-500">
                  {r.kind === 'offer' && 'opened'}
                  {r.kind === 'counter' && 'countered'}
                  {r.kind === 'accept' && 'accepted'}
                  {r.kind === 'decline' && 'declined'}
                  {r.kind === 'proceed' && 'paid'}
                  {r.kind === 'cancel' && 'cancelled'}
                  {r.kind === 'reschedule' && 'agreed a new time'}
                </span>
                {r.note && <p className="mt-0.5 text-xs text-gray-600">{r.note}</p>}
              </div>
              <span className="flex-shrink-0 font-mono text-sm font-bold">{money(r.amountCents)}</span>
            </div>
          ))}
        </div>

        {error && <div className="mt-4 border-2 border-red-600 bg-red-50 p-2 font-mono text-xs text-red-700">{error}</div>}

        {/* Negotiation actions */}
        {canNegotiate && !countering && (
          <div className="mt-4 space-y-2">
            <div className="flex gap-2">
              <button onClick={doAccept} disabled={busy}
                className="flex-1 border-2 border-black py-3 font-mono text-sm font-bold uppercase tracking-widest text-black hover:opacity-90 disabled:opacity-50"
                style={{ background: pink }}>
                {busy ? '…' : `Accept ${money(offer.currentAmountCents)}`}
              </button>
              <button onClick={() => { setCountering(true); setCounterAmount(String((offer.currentAmountCents / 100).toFixed(0))); }} disabled={busy}
                className="border-2 border-black bg-white px-4 py-3 font-mono text-sm font-bold uppercase tracking-widest hover:bg-gray-100 disabled:opacity-50">
                Counter
              </button>
            </div>
            <button onClick={doDecline} disabled={busy}
              className="w-full border-2 border-gray-300 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-gray-500 hover:border-black hover:text-black disabled:opacity-50">
              Decline
            </button>
          </div>
        )}

        {canNegotiate && countering && (
          <div className="mt-4 space-y-3 border-2 border-black bg-white p-4">
            <label className="block font-mono text-[11px] font-bold uppercase tracking-wider">Your counter</label>
            <div className="flex items-center border-2 border-black bg-white">
              <span className="px-3 font-mono text-sm text-gray-500">$</span>
              <input type="number" min="1" step="1" inputMode="decimal" value={counterAmount}
                onChange={(e) => setCounterAmount(e.target.value)}
                className="w-full bg-transparent py-3 pr-3 font-mono text-sm outline-none" />
            </div>
            <textarea value={counterNote} maxLength={1000} rows={2} onChange={(e) => setCounterNote(e.target.value)}
              placeholder="Why (optional)" className="w-full resize-none border-2 border-black bg-white px-3 py-2 font-mono text-xs outline-none" />
            <div className="flex gap-2">
              <button onClick={doCounter} disabled={busy}
                className="flex-1 border-2 border-black py-3 font-mono text-sm font-bold uppercase tracking-widest text-black hover:opacity-90 disabled:opacity-50"
                style={{ background: pink }}>
                {busy ? '…' : 'Send counter'}
              </button>
              <button onClick={() => setCountering(false)} disabled={busy}
                className="border-2 border-black bg-white px-4 py-3 font-mono text-sm font-bold uppercase tracking-widest hover:bg-gray-100 disabled:opacity-50">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Not-your-turn while live */}
        {isLive && !myTurn && !readOnly && (
          <p className="mt-4 text-center font-mono text-xs text-gray-500">
            Waiting on the other side to respond. You'll get a notification.
          </p>
        )}

        {/* Buyer: pay to lock it in */}
        {buyerCanPay && (
          <div className="mt-4 border-2 border-black bg-white p-4">
            <div className="mb-3 flex items-center justify-between font-mono text-sm font-bold uppercase tracking-wider">
              <span>Total on card</span>
              <span>{money(offer.proceedChargeCents)}</span>
            </div>
            {/* Itemised, because the total now carries a platform fee as well as
                Stripe's cut — a lump "processing" line would be hiding ours in
                with theirs. */}
            <dl className="mb-3 space-y-1 font-mono text-[11px] text-gray-500">
              <div className="flex justify-between gap-4">
                <dt>To the pro (escrow, released after the gig)</dt>
                <dd>{money(offer.currentAmountCents)}</dd>
              </div>
              {offer.buyerFeeCents > 0 && (
                <div className="flex justify-between gap-4">
                  <dt>Service fee</dt>
                  <dd>{money(offer.buyerFeeCents)}</dd>
                </div>
              )}
              <div className="flex justify-between gap-4">
                <dt>Processing</dt>
                <dd>{money(offer.stripeFeeCents ?? (offer.proceedChargeCents - offer.currentAmountCents - (offer.buyerFeeCents || 0)))}</dd>
              </div>
            </dl>
            <div className="border border-black p-3">
              {isStripeDevMode()
                ? <DevCardElement value={devBehavior} onChange={setDevBehavior} />
                : (
                    <CardPaymentSection
                      ref={cardRef}
                      amountCents={offer.proceedChargeCents}
                      currency="cad"
                      countryDefault="CA"
                      onValidityChange={setCardReady}
                      onWalletPaymentMethod={doWalletProceed}
                      disabled={paying}
                      label="PAY WITH CARD"
                    />
                  )}
            </div>
            <div className="mt-3">
              <Button type="button" onClick={doProceed} disabled={paying || (!isStripeDevMode() && !cardReady)}>
                {paying ? 'Processing…' : `Pay ${money(offer.proceedChargeCents)} & book`}
              </Button>
            </div>
          </div>
        )}

        {/* Owner: waiting on payment */}
        {isOwner && status === 'accepted' && (
          <div className="mt-4 text-center font-mono text-xs text-gray-500">
            <p>Price agreed. Waiting for the buyer to pay — the funds land in your escrow once they do.</p>
            {offer.providerFeeCents > 0 && (
              <p className="mt-2">
                You&apos;ll escrow <span className="font-bold text-black">{money(offer.providerNetCents)}</span>
                {' '}after the {money(offer.providerFeeCents)} platform fee.
              </p>
            )}
          </div>
        )}

        {/* Confirmed booking */}
        {status === 'confirmed' && offer.booking && (() => {
          const bk = offer.booking;
          const flagged = bk.escrowState === 'flagged' || bk.hasOpenFlag;
          const refunded = bk.escrowState === 'refunded';
          const split = bk.escrowState === 'resolved';
          const released = bk.escrowState === 'released';
          const canFlag = !readOnly && bk.escrowState === 'held' && !bk.hasOpenFlag;
          const heading = released ? 'Paid out 💸' : refunded ? 'Refunded' : split ? 'Resolved' : flagged ? 'Under review ⏳' : 'Booked 🎉';
          return (
            <div className="mt-4 border-2 p-4" style={{ borderColor: flagged ? '#9ca3af' : pink }}>
              <div className="font-mono text-sm font-bold uppercase tracking-wider" style={{ color: flagged ? '#6b7280' : pink }}>{heading}</div>
              {(() => {
                const f = bk.openFlag;
                // On this page the viewer is always one of the two parties, so the filer
                // is simply "the party the flag is NOT against".
                const isRespondent = f && ((f.againstSide === 'owner') === isOwner);
                const escalated = f && f.ladderTier >= 1;
                let flaggedCopy = 'A problem was reported. The payout is on hold.';
                if (escalated) flaggedCopy = 'This is with our team for a decision. The payout stays on hold until it’s settled.';
                else if (flagged && isRespondent) flaggedCopy = 'A problem was reported on this booking. Make it right or contest it — the payout is on hold until it’s settled.';
                else if (flagged) flaggedCopy = 'Your report is with the other party. If they don’t make it right, you can escalate it to our team.';
                return (
                  <p className="mt-1 font-mono text-xs text-gray-600">
                    {released && `${money(bk.amountCents)} released to ${isOwner ? 'your' : 'the pro’s'} available balance.`}
                    {refunded && (bk.releasedSessions > 0
                      ? `The unpaid sessions were refunded to the buyer. ${bk.releasedSessions} session${bk.releasedSessions === 1 ? '' : 's'} stayed paid.`
                      : `${money(bk.amountCents)} was refunded to the buyer.`)}
                    {split && 'Resolved with a split — part released to the pro, part refunded to the buyer.'}
                    {flagged && flaggedCopy}
                    {!released && !refunded && !split && !flagged && (offer.sessionCount > 1
                      ? `${money(bk.amountCents)} paid for ${offer.sessionCount} sessions. Each session releases to the pro ${bk.holdHours}h after it ends.${isOwner && bk.releasedCents > 0 ? ` ${money(bk.releasedCents)} paid out so far.` : ''}`
                      : `${money(bk.amountCents)} held in escrow${offer.startsAt || bk.eventDate ? ` for ${bookingWhen({ startsAt: offer.startsAt, endsAt: offer.endsAt, eventDate: bk.eventDate }, { year: true })}` : ''}. Releases to the pro ${bk.holdHours}h after the gig.`)}
                  </p>
                );
              })()}

              {/* Ladder actions on an open flag. */}
              {flagged && bk.openFlag && bk.openFlag.ladderTier < 1 && ((bk.openFlag.againstSide === 'owner') === isOwner) && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" disabled={busy}
                    onClick={() => doRespond(bk.openFlag.id, 'make_right')}
                    className="border-2 border-black py-2 px-3 font-mono text-[10px] font-bold uppercase tracking-widest text-black hover:opacity-90 disabled:opacity-50" style={{ background: '#03FF00' }}>
                    {isOwner ? 'Make it right (full refund)' : 'Concede — release to pro'}
                  </button>
                  <button type="button" disabled={busy}
                    onClick={() => doRespond(bk.openFlag.id, 'contest')}
                    className="border-2 border-black bg-white py-2 px-3 font-mono text-[10px] font-bold uppercase tracking-widest hover:bg-gray-100 disabled:opacity-50">
                    Contest → our team
                  </button>
                </div>
              )}
              {flagged && bk.openFlag && ((bk.openFlag.againstSide === 'owner') !== isOwner) && (
                <button type="button" disabled={busy} onClick={() => doWithdraw(bk.openFlag.id)}
                  className="mt-3 border-2 border-gray-300 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:border-black hover:text-black disabled:opacity-50">
                  Withdraw report
                </button>
              )}

              {canFlag && (
                <button type="button" onClick={() => setShowFlag(true)}
                  className="mt-3 border-2 border-gray-300 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:border-black hover:text-black">
                  Report a problem
                </button>
              )}

              {/* Escrow-verified testimonial (6c) — buyer only, once released. */}
              {bk.canReview && (
                <div className="mt-4 border-t-2 border-dashed border-gray-300 pt-3">
                  <div className="mb-1 font-mono text-[11px] font-bold uppercase tracking-widest">Leave a verified review</div>
                  <div className="mb-2 flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onClick={() => setReviewRating(n)}
                        className="font-mono text-xl leading-none" style={{ color: n <= reviewRating ? pink : '#d1d5db' }}>★</button>
                    ))}
                  </div>
                  <textarea value={reviewText} maxLength={1000} rows={2} onChange={(e) => setReviewText(e.target.value)}
                    placeholder="How was it? (optional)" className="w-full resize-none border-2 border-black bg-white px-3 py-2 font-mono text-xs outline-none" />
                  <button type="button" disabled={busy} onClick={() => doReview(bk.id)}
                    className="mt-2 border-2 border-black py-2 px-4 font-mono text-[11px] font-bold uppercase tracking-widest text-black hover:opacity-90 disabled:opacity-50" style={{ background: pink }}>
                    Post review
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* Reschedule + cancel: the server says what this viewer may do (Nov 2026). */}
        <BookingChanges offer={offer} onChange={setOffer} pink={pink} />

        {/* A paid booking that was cancelled: where the money went. */}
        {status === 'canceled' && offer.booking && (
          <div className="mt-4 border-2 border-gray-400 bg-white p-4" data-testid="cancelled-booking">
            <div className="font-mono text-sm font-bold uppercase tracking-wider text-gray-600">Cancelled</div>
            <p className="mt-1 font-mono text-xs text-gray-600">
              {(() => {
                const last = [...offer.rounds].reverse().find((r) => r.kind === 'cancel');
                const who = last?.actor === 'owner' ? (isOwner ? 'You' : offer.businessName) : (isOwner ? 'The client' : 'You');
                const back = last?.amountCents > 0 ? ` ${money(last.amountCents)} went back to the card.` : '';
                const kept = offer.booking.escrowState === 'resolved' ? ' The rest stayed with the pro under their cancellation terms.' : '';
                return `${who} cancelled this booking.${back}${kept}`;
              })()}
            </p>
          </div>
        )}

        {terminal && !(status === 'canceled' && offer.booking) && (
          <p className="mt-4 text-center font-mono text-xs text-gray-500">This offer is {STATUS_COPY[status]?.toLowerCase()}.</p>
        )}
      </div>

      {offer.booking && (
        <FlagBookingModal isOpen={showFlag} onClose={() => setShowFlag(false)} bookingId={offer.booking.id} onFlagged={load} />
      )}
    </div>
  );
}
